import React from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import { costFormatter } from '@format/currency';
import { PillText, Tooltip } from '@web/atoms';
import checkIcon from '../../images/Icons/check-in-circle-lilac.svg';
import {
  Card,
  Info,
  StyledImage,
  Name,
  DescriptionMobile,
  DescriptionDesktop,
  CardContent,
  DisplayedPrice,
  StrikeThroughPrice,
  PricingSymbol,
  InactiveSymbol,
  ActiveSymbol,
  CardHeader,
  BadgesContainer,
  KeyInfo,
  PricesContainer,
  IconContainer,
  PriceText,
} from './styles.js';

const formatPrice = price => {
  if (!price || price == null || price === '' || isNaN(price)) {
    return '';
  }
  if (typeof price === 'string' && price.startsWith('$')) return price;

  const parsedPrice = parseFloat(price);

  if (isNaN(parsedPrice)) {
    return '';
  }

  return costFormatter(parsedPrice);
};

export const ProductCardCRO = ({
  label,
  title,
  priceSymbol,
  description,
  checked = false,
  img,
  onChange,
  benefit,
  unavailable,
  unavailableText,
  notSelectable,
  notSelectableText = 'To choose this item, please remove one of your previous selections.',
  pricingInfo = null,
  callout,
  showPriceText,
  priceText = 'From',
  showPricePerWeek = true,
  level,
  height,
  width,
  mobileHeight = 204,
  bgColor = 'lightest',
  showCheckmark = false,
  imageAlign = 'start',
  mobileDescriptionPosition = 'bottom',
}) => {
  const getPriceSymbol = () => {
    switch (priceSymbol) {
      case 'low':
        return (
          <PricingSymbol data-testid="pricingLow">
            <ActiveSymbol>$</ActiveSymbol>
            <InactiveSymbol>$$</InactiveSymbol>
          </PricingSymbol>
        );
      case 'mid':
        return (
          <PricingSymbol data-testid="pricingMid">
            <ActiveSymbol>$$</ActiveSymbol>
            <InactiveSymbol>$</InactiveSymbol>
          </PricingSymbol>
        );
      case 'high':
        return (
          <PricingSymbol data-testid="pricingHigh">
            <ActiveSymbol>$$$</ActiveSymbol>
          </PricingSymbol>
        );
      default:
        return (
          <PricingSymbol data-testid="pricingDefault">
            <InactiveSymbol>$$$</InactiveSymbol>
          </PricingSymbol>
        );
    }
  };

  const getProductCard = props => (
    <Card
      {...props}
      checked={checked}
      unavailable={unavailable}
      $notSelectable={notSelectable}
      $bgColor={bgColor}
      aria-label={`${label} ${title}`}
      onClick={onChange}
      height={height}
      $width={width}
      $mobileHeight={mobileHeight}
    >
      <CardContent $imageAlign={imageAlign}>
        {!!img && (
          <StyledImage
            checked={checked}
            unavailable={unavailable}
            $imageAlign={imageAlign}
          >
            <NextImage src={img.src} alt={img.alt} width={72} height={72} />
          </StyledImage>
        )}
        <Info $hasImage={!!img}>
          <CardHeader>
            <KeyInfo $showCheckmark={showCheckmark} $imageAlign={imageAlign}>
              <Name as={level} $showCheckmark={showCheckmark}>
                {title}
              </Name>
              {showCheckmark && checked && (
                <IconContainer>
                  <NextImage
                    src={checkIcon}
                    width={28}
                    height={28}
                    alt={'Check icon'}
                  />
                </IconContainer>
              )}
              {(benefit || callout) && (
                <BadgesContainer>
                  {benefit && (
                    <PillText text={benefit} variant="success" size="small" />
                  )}
                  {callout && (
                    <PillText
                      text={callout}
                      variant="success"
                      size="small"
                    ></PillText>
                  )}
                </BadgesContainer>
              )}
            </KeyInfo>
          </CardHeader>
          {priceSymbol && getPriceSymbol()}
          {pricingInfo &&
            showPricePerWeek &&
            !unavailable &&
            Boolean(pricingInfo.price) && (
              <PricesContainer>
                {showPriceText && <PriceText>{priceText}</PriceText>}
                {pricingInfo.discount && (
                  <StrikeThroughPrice>
                    {`${formatPrice(pricingInfo.price)} `}
                  </StrikeThroughPrice>
                )}
                <DisplayedPrice>
                  {pricingInfo.discount
                    ? `${formatPrice(pricingInfo.discount)}/week`
                    : `${formatPrice(pricingInfo.price)}/week`}
                </DisplayedPrice>
              </PricesContainer>
            )}

          <DescriptionDesktop
            $mobileDescriptionPosition={mobileDescriptionPosition}
          >
            {unavailable ? unavailableText : description}
          </DescriptionDesktop>
        </Info>
      </CardContent>
      <DescriptionMobile $mobileDescriptionPosition={mobileDescriptionPosition}>
        {unavailable ? unavailableText : description}
      </DescriptionMobile>
    </Card>
  );

  return notSelectable && !unavailable ? (
    <Tooltip trigger={getProductCard} placement="top" offsetY={-200}>
      {notSelectableText}
    </Tooltip>
  ) : (
    getProductCard()
  );
};

ProductCardCRO.displayName = 'ProductCardCRO';
ProductCardCRO.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  priceSymbol: PropTypes.string,
  benefit: PropTypes.string,
  unavailable: PropTypes.bool,
  unavailableText: PropTypes.string,
  notSelectable: PropTypes.bool,
  notSelectableText: PropTypes.string,
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  showCheckmark: PropTypes.bool,
  img: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  pricingInfo: PropTypes.shape({
    price: PropTypes.string,
    discount: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  callout: PropTypes.string,
  showPriceText: PropTypes.bool,
  priceText: PropTypes.string,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  showPricePerWeek: PropTypes.bool,
  height: PropTypes.number,
  width: PropTypes.number,
  mobileHeight: PropTypes.number,
  bgColor: PropTypes.oneOf(['base', 'lightest']),
  imageAlign: PropTypes.string,
  mobileDescriptionPosition: PropTypes.oneOf(['bottom', 'right']),
};
